// Lógica pura de la conexión de anuncios de Meta en el front: el candado de quién
// la ve, los mensajes de error y cómo leer lo que devuelve `ads-conexion`. Sin red
// ni DOM, para probarla con vitest. Los efectos viven en `metaAdsConexion.ts`.
// Ver ADR-2026-09-24-anuncios-de-meta-conexion.

/**
 * Mientras Meta no apruebe el acceso Avanzado (ads_read, ads_mcp_management,
 * business_management y public_profile), sólo pueden conectar las cuentas con rol
 * en la app: la fila de Mi marca y el chat se muestran únicamente a los dev
 * testers. Con la aprobación, esto pasa a `true`.
 */
export const ANUNCIOS_DE_META_PARA_TODOS = false;

export function veAnunciosDeMeta(isDevTester: boolean | null | undefined): boolean {
  return ANUNCIOS_DE_META_PARA_TODOS || isDevTester === true;
}

/** Se dispara en `window` al conectar o desconectar, para que el chat se entere. */
export const EVENTO_CAMBIO_DE_ANUNCIOS = "atlas:anuncios-cambio";

/** Clave donde `ads-conectado.html` deja el aviso por si se perdió el `opener`. */
export const CLAVE_AVISO_ANUNCIOS = "atlas:ads-conectado";

/** El `type` del postMessage de `ads-conectado.html`. */
export const TIPO_AVISO_ANUNCIOS = "ads-conectado";

export interface CuentaDeAnuncios {
  id: string;
  nombre: string;
  moneda: string | null;
  activa: boolean;
  negocio: string | null;
}

/** Lo que se ve de una cuenta en una línea: "Estudio Atlas 2026 · Atlas Software". */
export function nombreDeCuenta(c: Pick<CuentaDeAnuncios, "nombre" | "negocio"> | null | undefined): string {
  if (!c) return "";
  return c.negocio && c.negocio !== c.nombre ? `${c.nombre} · ${c.negocio}` : c.nombre;
}

/** Lee la lista de cuentas que devuelve `finalizar` cuando hay que elegir. Descarta lo raro. */
export function leerCuentas(data: unknown): CuentaDeAnuncios[] {
  if (!Array.isArray(data)) return [];
  return data
    .filter((c): c is Record<string, unknown> => !!c && typeof c === "object")
    .filter((c) => typeof c.id === "string" && /^\d+$/.test(c.id))
    .map((c) => ({
      id: c.id as string,
      nombre: typeof c.nombre === "string" && c.nombre ? c.nombre : `Cuenta ${c.id}`,
      moneda: typeof c.moneda === "string" ? c.moneda : null,
      activa: c.activa !== false,
      negocio: typeof c.negocio === "string" ? c.negocio : null,
    }));
}

export interface DetalleDelErrorDeAnuncios {
  marca?: string | null;
  cuenta?: string | null;
}

/** Qué decirle a la persona según el código de la edge o del popup. */
export function mensajeDeErrorDeAnuncios(code: string, det: DetalleDelErrorDeAnuncios = {}): string {
  const cuenta = det.cuenta ? `"${det.cuenta}"` : "Esa cuenta publicitaria";
  switch (code) {
    case "cancelado":
      return "Cancelaste la conexión en Facebook. Cuando quieras, probá de nuevo.";
    case "permisos":
      return "Para leer tus anuncios, Atlas necesita que aceptes todos los permisos en la ventana de Facebook. Probá de nuevo y dejalos marcados.";
    case "sin_cuentas":
      return "Tu Facebook no tiene ninguna cuenta publicitaria a la que puedas acceder. Si la cuenta es de tu empresa, pedile a quien la administra que te dé acceso.";
    case "claim_invalido":
    case "claim_vencido":
    case "tiempo":
      return "La conexión tardó demasiado y se venció. Probá de nuevo.";
    case "claim_perdido":
      return "Se cortó la conexión con Facebook antes de terminar. Probá de nuevo.";
    case "cambio_reciente":
      return "Alguien conectó otra cuenta en esta marca mientras conectabas. Revisá cuál quedó y, si querés cambiarla, probá de nuevo.";
    case "otro_usuario":
      return "Esta conexión la empezó otra persona. Iniciala de nuevo desde tu cuenta.";
    case "cuenta_en_otra_marca":
      return det.marca
        ? `${cuenta} ya está conectada a tu marca "${det.marca}". Elegí otra o desconectala ahí primero.`
        : `${cuenta} ya está conectada a otra marca. Elegí otra o desconectala ahí primero.`;
    case "cuenta_en_otra_empresa":
      return `${cuenta} ya está conectada a otra cuenta de Atlas. Elegí otra cuenta publicitaria.`;
    case "cuenta_invalida":
      return "Esa cuenta publicitaria no está entre las tuyas. Elegí una de la lista.";
    case "marca":
      return "No encontramos la marca. Recargá la página y probá de nuevo.";
    case "sin_sesion":
      return "Tu sesión venció. Volvé a iniciar sesión y probá de nuevo.";
    case "popup_bloqueado":
      return "Tu navegador bloqueó la ventana de Facebook. Permití las ventanas emergentes para Atlas y probá de nuevo.";
    case "ventana_cerrada":
      return "Se cerró la ventana de Facebook antes de terminar. Probá de nuevo.";
    case "sin_configurar":
    case "origen":
      return "La conexión con tus anuncios todavía no está disponible desde acá.";
    default:
      return "Meta no terminó de conectar tus anuncios. Probá de nuevo en un rato.";
  }
}

export class ErrorDeAnuncios extends Error {
  constructor(
    readonly code: string,
    det: DetalleDelErrorDeAnuncios = {},
    /** Si Meta devolvió varias cuentas, para volver a elegir sin pasar por Facebook. */
    readonly elegir: CuentaDeAnuncios[] = [],
  ) {
    super(mensajeDeErrorDeAnuncios(code, det));
    this.name = "ErrorDeAnuncios";
  }
}
